import React, { useState } from 'react';
import { BadgeCheck, Facebook, Instagram, Linkedin, Youtube, Twitter, Link as LinkIcon, X } from 'lucide-react';
import { iconFor } from './icons.js';
import CarrierLogo from './CarrierLogo.jsx';
import QuoteForm from './QuoteForm.jsx';

const SOCIALS = [
  ['facebook', Facebook], ['x', Twitter], ['instagram', Instagram],
  ['linkedin', Linkedin], ['youtube', Youtube], ['tiktok', LinkIcon],
];

const H2 = ({ children }) => <h2 className="text-center text-xl font-extrabold text-slate-900 mb-6">{children}</h2>;

const goTo = (id) => (e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

/** Sticky page header: who this is, plus anchors to the sections that exist. */
function Header({ m }) {
  const links = [
    ['Home', 'pp-home', true], ['About', 'pp-about', true], ['Products', 'pp-products', m.products.length > 0],
    ['Licenses', 'pp-licenses', !!m.nationalProducerNumber || m.licenses.length > 0],
    ['Carriers', 'pp-carriers', m.carriers.length > 0], ['Contact', 'pp-contact', m.showQuoteForm],
  ].filter(([, , on]) => on);
  return (
    <header className="sticky top-0 z-40 flex items-center gap-4 border-b border-slate-200 bg-white/95 px-4 py-2.5 backdrop-blur" data-testid="pp-header">
      <div className="flex min-w-0 items-center gap-2 text-sm font-bold text-slate-900">
        <BadgeCheck className="h-5 w-5 shrink-0 text-blue-500" />
        <span className="truncate">{m.about?.name || m.title}</span>
        {m.about?.occupation && <span className="hidden truncate text-xs font-medium text-slate-500 @lg:inline">· {m.about.occupation}</span>}
      </div>
      <nav className="ml-auto flex items-center gap-1 overflow-x-auto" aria-label="Page sections">
        {links.map(([label, id]) => (
          <a key={id} href={`#${id}`} onClick={goTo(id)} className="whitespace-nowrap rounded px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-600">{label}</a>
        ))}
      </nav>
    </header>
  );
}

function Hero({ m }) {
  const bg = m.hasBackground && m.images?.background;
  return (
    <section id="pp-home" className="scroll-mt-16 grid @2xl:grid-cols-2" data-testid="pp-hero">
      <div className="px-6 py-8 sm:px-10 @2xl:py-12">
        <h1 className="text-2xl @2xl:text-3xl font-extrabold text-slate-900 leading-tight">{m.headerTitle || 'Your header title'}</h1>
        {m.bookingUrl ? (
          <a href={m.bookingUrl} className="mt-4 inline-block rounded bg-blue-500 px-3 py-1.5 text-xs font-bold tracking-wide text-white">BOOK A CALL</a>
        ) : (
          <span className="mt-4 inline-block rounded bg-blue-500 px-3 py-1.5 text-xs font-bold tracking-wide text-white">BOOK A CALL</span>
        )}
        {m.stats.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6">
            {m.stats.map((s, i) => {
              const Icon = iconFor(s.icon);
              return (
                <div key={i} className="text-center">
                  <Icon className="mx-auto h-5 w-5 text-blue-500" />
                  <div className="mt-1 text-sm font-bold text-slate-900">{s.title}</div>
                  <div className="text-[11px] leading-snug text-slate-600">{s.description}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {bg && (
        <div className="relative min-h-48 @2xl:min-h-[22rem]">
          <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover @2xl:[clip-path:polygon(12%_0,100%_0,100%_100%,0_100%)]" />
        </div>
      )}
    </section>
  );
}

function About({ m }) {
  const a = m.about;
  const links = SOCIALS.filter(([k]) => a.socials?.[k]);
  return (
    <section id="pp-about" className="scroll-mt-16 px-6 py-8 sm:px-10" data-testid="pp-about">
      <div className="mx-auto flex max-w-xl flex-col items-center gap-6 @lg:flex-row @lg:items-start">
        <div className="flex flex-col items-center text-center shrink-0">
          {m.hasProfile && m.images?.profile
            ? <img src={m.images.profile} alt={a.name} className="h-32 w-32 rounded-full object-cover" />
            : <div className="flex h-32 w-32 items-center justify-center rounded-full bg-slate-300 text-xs text-slate-600">Upload to preview</div>}
          <div className="mt-3 text-sm font-bold text-slate-900">{a.name || 'Your name'}</div>
          <div className="text-xs text-slate-500">{a.occupation || 'Your occupation'}</div>
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">About Me</h2>
          <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-slate-700">{a.text || 'About me text'}</p>
          {links.length > 0 && (
            <div className="mt-3 flex gap-3">
              {links.map(([k, Icon]) => (
                <a key={k} href={a.socials[k]} target="_blank" rel="noopener noreferrer nofollow" aria-label={k} className="text-slate-500 hover:text-blue-600"><Icon className="h-4 w-4" /></a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Products({ m }) {
  return (
    <section id="pp-products" className="scroll-mt-16 px-6 py-8 sm:px-10" data-testid="pp-products">
      <H2>We Got You Covered</H2>
      <div className="mx-auto grid max-w-2xl gap-6 @lg:grid-cols-2">
        {m.products.map((p) => {
          const Icon = iconFor(p.icon);
          return (
            <div key={p.id} className="flex gap-3">
              <Icon className="mt-0.5 h-7 w-7 shrink-0 text-blue-500" />
              <div>
                <div className="text-xs font-bold text-slate-900">{p.title}</div>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{p.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Licenses({ m, licenseUrl }) {
  const [open, setOpen] = useState(null);
  const npn = m.nationalProducerNumber;
  return (
    <section id="pp-licenses" className="scroll-mt-16 px-6 py-8 sm:px-10" data-testid="pp-licenses">
      <H2>State Licenses &amp; Authorizations</H2>
      <div className="flex flex-wrap justify-center gap-3">
        {m.licenses.map((l) => {
          const label = (
            <>
              <BadgeCheck className={`h-4 w-4 shrink-0 ${l.hasCertificate || l.licenseNumber ? 'text-blue-500' : 'text-slate-300'}`} />
              <span className="flex flex-col items-start text-left leading-tight">
                <span className="font-medium">{l.name}</span>
                {l.licenseNumber && (
                  <span className="text-[10px] text-slate-500" data-testid={`pp-badge-${l.state}`}>
                    State License ID: {l.licenseNumber}{npn ? ` · NPN: ${npn}` : ''}
                  </span>
                )}
                {l.hasCertificate && <span className="text-[10px] text-blue-600">View certificate</span>}
              </span>
            </>
          );
          const cls = 'flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-xs text-slate-800 shadow-md';
          return l.hasCertificate && licenseUrl
            ? <button key={l.state} type="button" onClick={() => setOpen(l)} className={`${cls} hover:shadow-lg`}>{label}</button>
            : <div key={l.state} className={cls}>{label}</div>;
        })}
      </div>
      {npn && <p className="mt-6 text-center text-xs text-slate-700">National producer #: {npn}</p>}
      {open && licenseUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-label={`${open.name} license`} onClick={() => setOpen(null)}>
          <div className="relative max-h-full max-w-2xl overflow-auto rounded-lg bg-white p-3" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Close" className="absolute right-2 top-2 z-10 rounded-full bg-white p-1 shadow" onClick={() => setOpen(null)}><X className="h-4 w-4" /></button>
            {open.certificateType === 'pdf' ? (
              <>
                <iframe title={`${open.name} license`} src={licenseUrl(open.state)} className="h-[75vh] w-[min(88vw,40rem)] border-0" />
                <a href={licenseUrl(open.state)} target="_blank" rel="noopener noreferrer" className="mt-2 block text-center text-xs text-blue-600 underline">Open the PDF in a new tab</a>
              </>
            ) : (
              <img src={licenseUrl(open.state)} alt={`${open.name} license`} className="max-h-[80vh] max-w-full" />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Carriers({ m }) {
  return (
    <section id="pp-carriers" className="scroll-mt-16 px-6 py-8 sm:px-10" data-testid="pp-carriers">
      <H2>Industry's Best Insurance Providers</H2>
      <div className="flex flex-wrap items-start justify-center gap-5">
        {m.carriers.map((c) => <CarrierLogo key={c.id} carrier={c} />)}
      </div>
    </section>
  );
}

function Contact({ m, slug }) {
  return (
    <section id="pp-contact" className="scroll-mt-16 px-6 py-8 sm:px-10" data-testid="pp-contact">
      <div className="mx-auto grid max-w-2xl gap-6 @lg:grid-cols-[1fr_1.6fr]">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Request a Quote</h2>
          <p className="mt-3 text-xs leading-relaxed text-slate-600">Just fill out the form with your contact information and we'll get back to you as soon as possible.</p>
        </div>
        <QuoteForm slug={slug} />
      </div>
    </section>
  );
}

/**
 * The Producer Page itself — one renderer for the wizard preview and the public page.
 * @param {{model: object, only?: string, slug?: string}} props
 *   `only` renders a single section (wizard preview); omitted renders the whole page.
 *   `slug` makes the quote form live (public page); empty keeps it inert (preview).
 */
export default function ProducerPageView({ model: m, only, slug = '' }) {
  const show = (id, has = true) => (!only || only === id) && has;
  const licenseUrl = slug ? (state) => `${import.meta.env.VITE_API_URL || '/api'}/producer-page/public/${encodeURIComponent(slug)}/license/${state}` : m.images?.license;
  return (
    <div className="@container bg-white text-slate-900 font-sans" data-testid="producer-page-view">
      {!only && <Header m={m} />}
      {show('title') && <Hero m={m} />}
      {show('about') && <About m={m} />}
      {show('products', m.products.length > 0) && <Products m={m} />}
      {show('licenses', !!m.nationalProducerNumber || m.licenses.length > 0) && <Licenses m={m} licenseUrl={licenseUrl} />}
      {show('carriers', m.carriers.length > 0) && <Carriers m={m} />}
      {show('contact', m.showQuoteForm) && <Contact m={m} slug={slug} />}
    </div>
  );
}
